// @flow

import type {NotificationNode, EditableGraph, Id} from 'src/types/chatbot';
import type {EntityType} from 'src/types/ats-entities';

import * as React from 'react';

import {useReleaseFlag} from 'src/hooks/product-flags';
import useI18n from 'src/hooks/useI18n';

import {useAtsVariables, useDraftDecorator, useEditorState} from './reducer';
import {keyBy} from 'src/utils/map';
import {validateNode} from './validation';

import {singleDynamicRegex} from 'src/components/lib/markdown-editor/dynamic-text.jsx';
import VariablePickerSelect from 'src/components/lib/variable-picker/select-2.jsx';
import {findAncestorNodeVariables} from '../utils';
import EditorContainer from './container.jsx';
import {
  UncontrolledDraftEditor,
  prepareForServer,
  nodeVariableDecorator,
} from 'src/components/conversation-builder/draft-editor.jsx';
import {FormPair} from 'src/components/conversation-builder/form.jsx';
import {PadSection} from './common.jsx';

import css from 'src/components/conversation-builder/popover.css';


export default function NotificationEditor({
  graph,
  entityType,
  defaultNode,
  hasSubmitted,
  edgeId,
  ...props
}: {
  graph: EditableGraph,
  entityType: EntityType,
  onClose: () => mixed,
  onSubmit: (NotificationNode) => mixed,
  defaultNode: NotificationNode,
  actionName: 'add' | 'edit',
  edgeId?: Id,
  hasSubmitted: boolean,
  readOnly: boolean,
}): React.Node {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const {readOnly} = props;

  const dynamicLabels = useAtsVariables(entityType);
  const decorator = useDraftDecorator(dynamicLabels, [nodeVariableDecorator]);
  const [state, dispatch] = useEditorState({
    defaultNode,
  });

  const update = (payload) => dispatch({type: 'update', payload});
  const {node} = state;

  const emailDynamicLabels = React.useMemo(
    () =>
      dynamicLabels.filter((label) => ['string', 'email'].includes(label.type)),
    [dynamicLabels],
  );
  const dynamicLabelMap = React.useMemo(() => keyBy(dynamicLabels, 'value'));

  const error = hasSubmitted ? validateNode(node, graph) : null;
  const showFileUploadNodeVar = Boolean(
    useReleaseFlag('chatbotFileUploadLinkNotificationEnabled'),
  );
  const nodeVariables = findAncestorNodeVariables(
    graph,
    defaultNode,
    edgeId,
    showFileUploadNodeVar,
  );

  return (
    <EditorContainer onUpdate={update} state={state} error={error} {...props}>
      <PadSection>
        <FormPair label={t('Recipients', 'Recipients')} TagName="div">
          <VariablePickerSelect
            allowArbitraryValues
            classNames={{container: css.variablePicker}}
            baseEntityType={entityType}
            values={node.notification_recipients.map((recipient) => {
              const match = recipient.match(singleDynamicRegex);
              return (
                (match && dynamicLabelMap.get(match[1])) || {
                  value: recipient,
                  label: recipient,
                  arbitrary: true,
                }
              );
            })}
            options={emailDynamicLabels}
            onChange={(options) => {
              update({
                notification_recipients: options.map((option) =>
                  option.template
                    ? option.template.replace('$', option.value)
                    : option.value,
                ),
              });
            }}
            disabled={readOnly}
          />
        </FormPair>

        <FormPair label={t('Subject', 'Subject')} TagName="div">
          <UncontrolledDraftEditor
            placeholder={`${t('Enter_a_subject', 'Enter a subject')}...`}
            defaultValue={node.notification_email_subject}
            onChange={(editorState) => {
              update({
                notification_email_subject: prepareForServer(
                  editorState,
                  dynamicLabels,
                ),
              });
            }}
            dynamicLabels={dynamicLabels}
            decorator={decorator}
            entityType={entityType}
            isOneLine
            readOnly={readOnly}
            useCase={graph.use_case}
            nodeVariables={nodeVariables}
          />
        </FormPair>

        <FormPair label={t('Message', 'Message')} TagName="div">
          <UncontrolledDraftEditor
            placeholder={`${t('Enter_a_message', 'Enter a message')}...`}
            defaultValue={node.notification_text_content}
            onChange={(editorState) =>
              update({
                notification_text_content: prepareForServer(
                  editorState,
                  dynamicLabels,
                ),
              })
            }
            dynamicLabels={dynamicLabels}
            decorator={decorator}
            entityType={entityType}
            readOnly={readOnly}
            useCase={graph.use_case}
            nodeVariables={nodeVariables}
          />
        </FormPair>
      </PadSection>
    </EditorContainer>
  );
}
